import { Beacon, normalizeSubscriber, OBSERVABLE_PROPERTY_NAME, } from "../Observable/mod.js";
import { strictEqual } from "../utils/mod.js";
import { __isKnytReference } from "./typeGuards.js";
/**
 * A basic `Reference` implementation.
 *
 * @internal scope: workspace
 */
/*
 * ### Private Remarks
 *
 * There are three occurrences in which a reference will notify subscribers:
 * 1. When the reference is created, the initial value is set.
 * 2. When the reference is updated, the new value is set.
 * 3. When a subscriber subscribes to the reference, the current value is
 *    immediately sent to the subscriber.
 */
export class BasicReference {
    /**
     * The `Beacon` instance used to notify subscribers.
     */
    #emitter = Beacon.withEmitter();
    /**
     * @remarks
     *
     * The initial value is set in the constructor by the `set` method.
     * This is done to ensure that the `onUpdate` callback is called
     * when the initial value is set.
     *
     * @internal scope: package
     */
    #currentValue;
    /**
     * @internal scope: package
     */
    #onUpdate;
    #comparator;
    constructor(options) {
        const { initialValue, onUpdate, comparator } = options;
        this.#onUpdate = onUpdate;
        this.#comparator = comparator;
        // Immediately set the initial value after the ref object is constructed
        this.#set(initialValue, true);
    }
    get value() {
        return this.#currentValue;
    }
    set value(nextValue) {
        this.set(nextValue);
    }
    // This is a detachable method.
    get = () => {
        return this.#currentValue;
    };
    // This is a detachable method.
    set = (nextValue) => {
        this.#set(nextValue);
    };
    // NOTE: This is not considered detachable method, even though it is an arrow function.
    next = this.set;
    /**
     * A set of temporary subscribers used to track subscriptions during the
     * initial subscription phase. This prevents emitting the current value to
     * subscribers that are already being notified of a change, avoiding duplicate
     * notifications when subscribing during an update.
     *
     * Keeping subscribers in this set ensures they are not garbage collected
     * before the initial notification, preventing missed updates in rare cases.
     *
     * Subscribers are removed from this set once the initial subscription phase
     * is finished.
     */
    #trackers = new Set();
    subscribe(subscriber) {
        /**
         * Indicates whether the beacon has emitted a value
         * since the subscription started.
         */
        let hasEmitted = false;
        const tracker = () => {
            hasEmitted = true;
        };
        this.#trackers.add(tracker);
        const trackerSubscription = this.#emitter.beacon.subscribe(tracker);
        const subscription = this.#emitter.beacon.subscribe(subscriber);
        // All side-effects should be asynchronous.
        // A Ref defers side-effects until the next microtask.
        queueMicrotask(() => {
            this.#trackers.delete(tracker);
            trackerSubscription.unsubscribe();
            // Prevent sending the current value to the subscriber if the beacon
            // has already emitted a value since the subscription began.
            //
            // This avoids duplicate notifications, which can happen if a subscriber
            // is added during an ongoing change. Without this check, the subscriber
            // would receive the current value both immediately and again when the
            // change is processed.
            //
            // This scenario often occurs when a subscriber is registered
            // synchronously right after a reference is created and its initial value
            // is set.
            if (hasEmitted)
                return;
            // Send the current value to the subscriber immediately.
            // This is done to ensure that the subscriber is always up-to-date.
            normalizeSubscriber(subscriber).next(this.#currentValue);
        });
        return subscription;
    }
    asReadonly() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const ref = this;
        return {
            get value() {
                return ref.value;
            },
            get: ref.get,
            subscribe: (subscriber) => {
                return ref.subscribe(subscriber);
            },
            get [__isKnytReference]() {
                return true;
            },
            asInterop() {
                return ref.asInterop();
            },
            [Symbol.observable]() {
                return ref.asInterop();
            },
            [OBSERVABLE_PROPERTY_NAME]() {
                return ref.asInterop();
            },
        };
    }
    get [__isKnytReference]() {
        return true;
    }
    #set(nextValue, isInitialValue = false) {
        const comparator = this.#comparator ?? strictEqual;
        const previousValue = this.#currentValue;
        // The next value is always set, regardless of the comparator.
        // The comparator is used to determine if change notification
        // should be emitted. Mutating the current value is a separate
        // concern that is not related to the comparator.
        this.#currentValue = nextValue;
        if (
        // Always emit a change notification for the initial value to ensure
        // it is sent to subscribers, regardless of the comparator result.
        // This guarantees the first value is always delivered.
        //
        // Check `isInitialValue` first to avoid passing a non-existent
        // previous value to the comparator. This ensures the comparator
        // only compares valid previous and next values, preventing
        // unexpected behavior.
        !isInitialValue &&
            comparator(previousValue, nextValue)) {
            // If the comparator returns true, no change notification is emitted.
            return;
        }
        // All side-effects should be asynchronous.
        // A Ref defers side-effects until the next microtask.
        queueMicrotask(() => {
            this.#onUpdate?.(nextValue, previousValue);
            this.#emitter.next(nextValue);
        });
    }
    asInterop() {
        return this.#emitter.beacon.asInterop();
    }
    [Symbol.observable]() {
        return this.asInterop();
    }
    [OBSERVABLE_PROPERTY_NAME]() {
        return this.asInterop();
    }
}
